// Neo's receipt for the owner's own panel looks (TOM-595, cloud ADR 0364): what
// one request changed in the looks only this owner sees, built from the turn's
// `looks` records and never from Neo's words, with one Undo. A request that
// also changed a board keeps that board's receipt (neoReceipt.ts): a board is
// shared, these looks are not, and the two never share a card. They share one
// Undo: either card's undoes the whole request through the turn's undo route,
// so neither undoes only half of it (wholeTurnUndoRefs).
//
// Pure, apart from the step read and the undo request.

import { api } from '../api';
import { parseBoardLooksState, undoBoardLooks } from './boardLooks';
import {
  receiptTurnRef,
  type ReceiptLine,
  type ReceiptLineKind,
  type ReceiptPart,
  type TurnUndoAnswer,
} from './neoReceipt';
import type { ReceiptStep } from './receiptStep';

/**
 * One change Neo made to the owner's looks, as the `looks` event and the
 * conversation read carry it (`NeoLookChange`, TOM-592). TOM-594 adds the
 * fields after `namedId`; they are optional here until it lands, and a record
 * without them still makes a receipt.
 */
export interface NeoLookReceiptChange {
  boatId: string;
  /** The looks' revision after this change: the undo's `revision`. */
  revision: number;
  /** The version row's action (`look_added`, `named_changed`, `undone`, …). */
  action: string;
  lookId: string | null;
  namedId: string | null;
  scope: 'item' | 'board' | 'boat' | null;
  boardSlug: string | null;
  boardTitle: string | null;
  turnRef: string | null;
  /** The server's own sentence for the change; null on a record from before TOM-594. */
  summary: string | null;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function stringOf(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

/** At most this many characters of a summary are shown: it is one sentence. */
const SUMMARY_MAX = 200;

/**
 * Decode a `looks` event (`{ changes }`) or an answer's `looks` records read
 * back with the conversation. A record that is not one is dropped; a payload
 * that is not one is no records, so no receipt and nothing thrown.
 */
export function parseLookChanges(payload: unknown): NeoLookReceiptChange[] {
  const list = isRecord(payload) ? payload.changes : payload;
  const out: NeoLookReceiptChange[] = [];
  for (const raw of Array.isArray(list) ? list : []) {
    if (!isRecord(raw)) continue;
    const boatId = stringOf(raw.boatId);
    const action = stringOf(raw.action);
    const revision = raw.revision;
    if (boatId === undefined || action === undefined) continue;
    if (typeof revision !== 'number' || !Number.isInteger(revision) || revision < 0) continue;
    const scope =
      raw.scope === 'item' || raw.scope === 'board' || raw.scope === 'boat' ? raw.scope : null;
    const summary = stringOf(typeof raw.summary === 'string' ? raw.summary.trim() : undefined);
    out.push({
      boatId,
      revision,
      action,
      lookId: stringOf(raw.lookId) ?? null,
      namedId: stringOf(raw.namedId) ?? null,
      scope,
      boardSlug: stringOf(raw.boardSlug) ?? null,
      boardTitle: stringOf(raw.boardTitle) ?? null,
      turnRef: stringOf(raw.turnRef) ?? null,
      summary: summary === undefined ? null : summary.slice(0, SUMMARY_MAX),
    });
  }
  return out;
}

function where(change: NeoLookReceiptChange): string {
  if (change.scope === 'boat') return ' on all your boards';
  if (change.scope === 'board') return change.boardTitle === null ? '' : ` on ${change.boardTitle}`;
  if (change.scope === 'item') {
    return change.boardTitle === null ? ' on one panel' : ` on one panel on ${change.boardTitle}`;
  }
  return '';
}

/**
 * A change's line: the server's sentence, or, for a record without one, a
 * plain one built from what the record says.
 */
export function lookChangeSummary(change: NeoLookReceiptChange): string {
  if (change.summary !== null) return change.summary;
  switch (change.action) {
    case 'look_added':
      return `Added a look${where(change)}`;
    case 'look_changed':
      return `Changed a look${where(change)}`;
    case 'look_removed':
      return `Removed a look${where(change)}`;
    case 'named_added':
      return 'Saved a named panel';
    case 'named_changed':
      return 'Changed a named panel';
    case 'named_removed':
      return 'Removed a named panel';
    case 'undone':
      return 'Undid the last change to your looks';
    default:
      return 'Changed your looks';
  }
}

function lineKind(action: string): ReceiptLineKind {
  if (action === 'look_added' || action === 'named_added') return 'added';
  if (action === 'look_removed' || action === 'named_removed') return 'removed';
  if (action === 'undone') return 'plain';
  return 'changed';
}

/** The owner's looks on one boat that one request changed. */
export interface LooksReceiptPart {
  key: string;
  boatId: string;
  heading: string;
  lines: ReceiptLine[];
  /** The looks' revision after the request's last change: Undo's compare-and-swap. */
  revision: number;
  /** The request's turn, when the records name it: the step must still be its own. */
  turnRef: string | null;
}

/** One request's private receipt: a part per boat whose looks it changed, in order. */
export function buildLooksReceipt(changes: readonly NeoLookReceiptChange[]): LooksReceiptPart[] {
  const byBoat = new Map<string, NeoLookReceiptChange[]>();
  for (const change of changes) {
    const list = byBoat.get(change.boatId);
    if (list === undefined) byBoat.set(change.boatId, [change]);
    else list.push(change);
  }
  return [...byBoat.values()].map((records) => {
    const first = records[0]!;
    // Each line is a sentence that already says what happened, so a screen
    // reader reads it as it is shown.
    const lines = records.map((r, i): ReceiptLine => {
      const text = lookChangeSummary(r);
      return { key: `${r.revision}-${i}`, kind: lineKind(r.action), text, spoken: text };
    });
    const undoneOnly = records.every((r) => r.action === 'undone');
    return {
      key: `looks:${first.boatId}`,
      boatId: first.boatId,
      heading: undoneOnly ? 'Undid your last look change' : 'Changed your looks',
      lines,
      revision: Math.max(...records.map((r) => r.revision)),
      turnRef: records.find((r) => r.turnRef !== null)?.turnRef ?? null,
    };
  });
}

/** The line under the heading: whose screens it reaches. */
export function looksReceiptScope(boatName: string | null): string {
  return boatName === null ? 'Only you see this' : `Only you see this · ${boatName}`;
}

/**
 * Which parts of each private receipt a later request has superseded: the
 * looks keep one undo step per owner per boat (ADR 0364 §6), so an earlier
 * receipt for the same boat offers no Undo. Receipts in conversation order.
 */
export function supersededLooksParts(
  receipts: readonly (readonly LooksReceiptPart[])[]
): Set<string>[] {
  const later = new Set<string>();
  const out = receipts.map(() => new Set<string>());
  for (let i = receipts.length - 1; i >= 0; i--) {
    for (const part of receipts[i]!) {
      if (later.has(part.key)) out[i]!.add(part.key);
    }
    for (const part of receipts[i]!) later.add(part.key);
  }
  return out;
}

/**
 * Whether the part's Undo still applies, from the owner's looks as they are:
 * the step is set, the looks are still at the revision the request left them,
 * and the step is still that request's.
 */
export function looksStepOf(part: LooksReceiptPart, body: unknown): 'applies' | 'gone' {
  const state = parseBoardLooksState(body);
  if (state === null || state.undo === null || state.revision !== part.revision) return 'gone';
  if (part.turnRef !== null && state.undo.turnRef !== part.turnRef) return 'gone';
  return 'applies';
}

function statusOf(err: unknown): number | undefined {
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
}

/** Ask the looks route whether the part's Undo applies (TOM-381's check, for the looks). */
export async function looksReceiptStep(part: LooksReceiptPart): Promise<ReceiptStep> {
  try {
    return looksStepOf(part, await api<unknown>(`/v1/boats/${part.boatId}/looks`));
  } catch (err) {
    return statusOf(err) === 404 ? 'gone' : 'unknown';
  }
}

/** What a part's undo came to. */
export type LooksUndoOutcome =
  | { kind: 'undone' }
  | { kind: 'refused'; reason: 'changed' | 'nothing' }
  | { kind: 'failed' };

/** Undo one part: the owner's looks on its boat, as they were before the request. */
export async function undoLooksPart(
  part: LooksReceiptPart,
  idempotencyKey: string
): Promise<LooksUndoOutcome> {
  const outcome = await undoBoardLooks(part.boatId, part.revision, idempotencyKey);
  switch (outcome.kind) {
    case 'done':
      return { kind: 'undone' };
    case 'conflict':
      return { kind: 'refused', reason: 'changed' };
    case 'missing':
      return { kind: 'refused', reason: 'nothing' };
    case 'failed':
      return { kind: 'failed' };
  }
}

/** What the receipt says once a part's undo has an answer. */
export function looksOutcomeText(outcome: LooksUndoOutcome): string {
  if (outcome.kind === 'failed') {
    return 'Couldn’t undo. Nothing changed. Check the connection and try again.';
  }
  if (outcome.kind === 'refused') {
    return outcome.reason === 'changed'
      ? 'Your looks changed since, so this can’t be undone here.'
      : 'There’s nothing left to undo here. It was undone, or changed since.';
  }
  return 'Undone. Your looks are back as they were before this request.';
}

/** What a superseded part says in place of Undo. */
export const LOOKS_SUPERSEDED_TEXT = 'Your looks changed again since, so Undo isn’t offered.';

/** The one request every part names, or null when a part names none or they name more than one. */
export function looksTurnRef(parts: readonly LooksReceiptPart[]): string | null {
  const refs = new Set(parts.map((p) => p.turnRef));
  return refs.size === 1 ? ([...refs][0] ?? null) : null;
}

/**
 * The requests whose Undo is the whole request's (TOM-595): one that changed
 * what the boat's owners share (a board, an answer, a name) with an Undo to
 * offer, and the owner's own looks. Both its receipts' Undo go to the turn's
 * undo route (`undoTurn`), so neither undoes only half. Any other request
 * keeps each receipt's own undo routes. Takes each shared receipt's parts and
 * each private receipt's parts.
 */
export function wholeTurnUndoRefs(
  shared: readonly (readonly ReceiptPart[])[],
  looks: readonly (readonly LooksReceiptPart[])[]
): Set<string> {
  const sharedRefs = new Set(shared.map(receiptTurnRef));
  const out = new Set<string>();
  for (const parts of looks) {
    const ref = looksTurnRef(parts);
    if (ref !== null && sharedRefs.has(ref)) out.add(ref);
  }
  return out;
}

/** What the whole-request undo came to for the owner's looks on one boat. */
export function looksTurnOutcome(part: LooksReceiptPart, answer: TurnUndoAnswer): LooksUndoOutcome {
  if (answer.kind === 'failed') return { kind: 'failed' };
  const ours = (e: { part: string; boatId: string }) =>
    e.part === 'looks' && e.boatId === part.boatId;
  if (answer.undone.some(ours)) return { kind: 'undone' };
  const left = answer.notUndone.find(ours);
  return { kind: 'refused', reason: left?.reason === 'changed_since' ? 'changed' : 'nothing' };
}
